import styled from 'styled-components';
import { StyledWrapper } from '../styled';
import { fontDisplay, fontUi, viewMoreLink } from '../../theme/typography';

export const StyledPage = styled(StyledWrapper)`
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: stretch;
  width: 100%;
`;

export const StyledHero = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 112px;
  margin-bottom: 24px;
  padding: 22px 28px;
  border-radius: ${({ theme }) => theme.cardRadius};
  background: #000;
  overflow: hidden;
  box-sizing: border-box;

  @media only screen and (max-width: 768px) {
    min-height: 96px;
    padding: 16px 18px;
    flex-wrap: wrap;
  }
`;

export const StyledHeroCopy = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;

  h1 {
    ${fontDisplay};
    margin: 0;
    color: #fff;
    font-size: 32px;
    letter-spacing: -0.02em;
    @media only screen and (max-width: 768px) {
      font-size: 24px;
    }
  }

  p {
    ${fontUi};
    margin: 0;
    color: rgba(255, 255, 255, 0.55);
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
`;

export const StyledHeroLink = styled.a`
  ${fontUi};
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  background: #fff;
  color: #000;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.01em;
  text-decoration: none;
  white-space: nowrap;
  &:hover {
    opacity: 0.9;
  }
  svg {
    display: block;
    width: 14px;
    height: 14px;
  }
`;

export const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 25px;
  align-items: start;
  width: 100%;
  margin-bottom: 24px;

  @media only screen and (max-width: 1199px) {
    grid-template-columns: 1fr;
  }
`;

export const StyledColumn = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

export const StyledCard = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.cardRadius};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
  box-sizing: border-box;
`;

export const StyledList = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 4px 0 8px;
`;

export const StyledRowLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  border-top: 1px solid ${({ theme }) => theme.borderSubtle};
  text-decoration: none;
  color: inherit;
  &:first-child {
    border-top: none;
  }
  &:hover {
    background: ${({ theme }) => theme.hover};
  }
  svg {
    display: block;
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    color: ${({ theme }) => theme.textFaint};
  }
`;

export const StyledRowMain = styled.span`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
`;

export const StyledLogo = styled.img`
  display: block;
  width: ${({ $size }) => $size || 40}px;
  height: ${({ $size }) => $size || 40}px;
  object-fit: contain;
  background: none;
  border: 0;
  flex-shrink: 0;
`;

export const StyledLogoStack = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  > *:not(:first-child) {
    margin-left: -10px;
  }
`;

export const StyledNameBlock = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const StyledName = styled.span`
  ${fontUi};
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
`;

export const StyledHint = styled.span`
  ${fontUi};
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.textFaint};
`;

export const StyledMeta = styled.span`
  ${fontUi};
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${({ theme, $open }) => ($open ? theme.success : theme.textFaint)};
  text-align: right;
`;

export const StyledHeadingLink = styled.a`
  ${viewMoreLink};
  svg {
    display: block;
    width: 12px;
    height: 12px;
  }
`;

export const StyledEmpty = styled.p`
  ${fontUi};
  margin: 0;
  padding: 20px 16px 24px;
  font-size: 13px;
  color: ${({ theme }) => theme.textMuted};
`;

export const StyledSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
`;

export const StyledSkeletonLine = styled.div`
  height: 14px;
  border-radius: 6px;
  background: ${({ theme }) => theme.borderSubtle};
  width: ${({ $width }) => $width || '100%'};
`;

export const StyledLinkGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
`;

export const StyledQuickLink = styled.a`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  min-width: 0;
  padding: 14px 16px;
  border-radius: ${({ theme }) => theme.cardRadius};
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.border};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
  text-decoration: none;
  color: inherit;
  &:hover {
    background: ${({ theme }) => theme.hover};
  }
  svg {
    display: block;
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    color: ${({ theme }) => theme.textMuted};
  }
`;

export const StyledQuickCopy = styled.span`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

export const StyledQuickLabel = styled.span`
  ${fontUi};
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
`;

export const StyledQuickHint = styled.span`
  ${fontUi};
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.textFaint};
`;

export const StyledWinnersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 24px;

  @media only screen and (max-width: 1199px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media only screen and (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const StyledWinnerCard = styled.a`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  padding: 14px 14px 16px;
  text-decoration: none;
  color: inherit;
  border-radius: ${({ theme }) => theme.cardRadius};
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.border};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
  &:hover {
    background: ${({ theme }) => theme.hover};
  }
`;

export const StyledWinnerBrand = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const StyledWinnerClub = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

export const StyledClubMark = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: none;
    border: 0;
  }
`;

export const StyledClubFallback = styled.span`
  ${fontDisplay};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 13px;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.textFaint};
`;

export const StyledWinnerMeta = styled.span`
  ${fontUi};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textFaint};
`;

export const StyledWinnerTeam = styled.span`
  ${fontDisplay};
  display: block;
  font-size: 15px;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.text};
  line-height: 1.2;
`;

export const StyledWinnerWho = styled.span`
  ${fontUi};
  display: block;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.textMuted};
`;
